import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, imageUrl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Camera, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function QrPoster() {
  const [params] = useSearchParams();
  const slug = params.get("store");
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(!!slug);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      try { const { data } = await api.get(`/stores/${slug}`); setStore(data); }
      catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [slug]);

  const targetUrl = useMemo(() => (
    slug ? `${window.location.origin}/store/${slug}` : `${window.location.origin}/register`
  ), [slug]);

  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=520x520&margin=8&data=${encodeURIComponent(targetUrl)}`;

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-10 text-slate-500" data-testid="poster-loading">Loading…</div>;
  if (slug && !store) return <div className="max-w-3xl mx-auto px-4 py-10 text-slate-500" data-testid="poster-404">Store not found.</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-10 print:py-0 print:px-0">
      <div className="flex justify-end gap-2 mb-4 print:hidden">
        <Button onClick={()=>window.print()} className="bg-emerald-800 hover:bg-emerald-900" data-testid="print-qr">
          <Printer className="w-4 h-4 mr-2" />Print poster
        </Button>
      </div>
      <Card className="border-emerald-200 shadow-lg print:shadow-none print:border-0">
        <CardContent className="py-10 text-center space-y-4 print:py-6">
          {store ? (
            <>
              {store.logo_path && (
                <img src={imageUrl(store.logo_path)} alt={store.store_name} className="w-24 h-24 rounded-2xl object-cover border-4 border-white shadow mx-auto" />
              )}
              <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-fuchsia-800 bg-fuchsia-100 px-3 py-1.5 rounded-full">
                <Camera className="w-3.5 h-3.5" />Markdowns · In-store today
              </div>
              <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900 leading-none">{store.store_name}</h1>
              {store.tagline && <p className="text-slate-600 italic max-w-md mx-auto">{store.tagline}</p>}
              <div className="pt-2 flex justify-center">
                <img src={qrUrl} alt={`Scan to view ${store.store_name} markdowns`} className="w-64 h-64 sm:w-80 sm:h-80 rounded-2xl border-4 border-white shadow-2xl bg-white" data-testid="poster-qr" />
              </div>
              <div className="pt-2 font-display text-2xl font-bold text-emerald-900">Scan to see today's deals</div>
              <div className="text-xs text-slate-500">Or visit <span className="font-mono">{window.location.host}/store/{slug}</span></div>
              <div className="text-[11px] text-slate-400 pt-4">Powered by Blurblive · markdowns.nz</div>
            </>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-emerald-800 bg-emerald-100 px-3 py-1.5 rounded-full">
                <Camera className="w-3.5 h-3.5" />For local shops · Aotearoa NZ
              </div>
              <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900 leading-none">Post your markdowns on <span className="text-emerald-800">Blurblive</span></h1>
              <p className="text-slate-600 max-w-md mx-auto">Snap a photo of your marked-down product. Post it live in seconds. Drive Kiwi shoppers straight to your door.</p>
              <div className="pt-2 flex justify-center">
                <img src={qrUrl} alt="Scan to register your store" className="w-64 h-64 sm:w-80 sm:h-80 rounded-2xl border-4 border-white shadow-2xl bg-white" data-testid="poster-qr" />
              </div>
              <div className="pt-2 font-display text-xl font-bold text-emerald-900">Scan to register your store</div>
              <div className="text-xs text-slate-500">Or visit <span className="font-mono">{targetUrl}</span></div>
              <div className="text-[11px] text-slate-400 pt-4">From $2 per listing · Fiver Deal $5 = 28 listings/month</div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
